/* Native scrolling, no autoplay or dependencies. Works with theme-editor reloads. */
if (!customElements.get('ml-drop-carousel')) {
  customElements.define('ml-drop-carousel', class extends HTMLElement {
    connectedCallback() {
      // Theme-editor HTML can connect the element before parsing its children.
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      this.track = this.querySelector('[data-drop-track]');
      this.slides = [...this.querySelectorAll('[data-drop-slide]')];
      this.dots = [...this.querySelectorAll('[data-drop-dot]')];
      if (!this.isConnected || !this.track || !this.slides.length || this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const headers = [...document.querySelectorAll('.ml-announcement, [data-site-header]')];
      this.headerObserver = new ResizeObserver(() => {
        const height = headers.reduce((total, header) => total + header.getBoundingClientRect().height, 0);
        this.style.setProperty('--drop-header-height', `${height}px`);
      });
      headers.forEach(header => this.headerObserver.observe(header));
      this.current = -1;
      this.update(0, false);
      this.dataset.dropReady = 'true';
      this.querySelector('[data-drop-prev]')?.addEventListener('click', () => this.go(this.current - 1), options);
      this.querySelector('[data-drop-next]')?.addEventListener('click', () => this.go(this.current + 1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index), options));
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const bounds = this.track.getBoundingClientRect();
          const nearest = this.slides.reduce((best, slide, index) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - bounds.left);
            return distance < best.distance ? { index, distance } : best;
          }, { index: 0, distance: Infinity });
          this.update(nearest.index, true);
        });
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.track.focus({ preventScroll: true });
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.current + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const selected = event.target.closest('[data-drop-slide]');
        const index = this.slides.indexOf(selected);
        if (index >= 0) this.go(index, false);
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.go(Math.max(this.current, 0), false));
      this.resizeObserver.observe(this.track);
    }

    go(index, animate = true) {
      const next = (index + this.slides.length) % this.slides.length;
      const target = this.slides[next];
      const left = this.track.scrollLeft + target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left, behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    }

    update(index, announce) {
      if (this.current === index) return;
      const outgoing = this.slides[this.current];
      if (outgoing?.contains(document.activeElement)) this.track.focus({ preventScroll: true });
      this.current = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-drop-count]');
      if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      const announcement = this.querySelector('[data-drop-announcement]');
      if (announce && announcement) announcement.textContent = `Campaign ${index + 1} of ${this.slides.length}: ${this.slides[index].dataset.dropName}`;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      delete this.dataset.dropReady;
      this.resizeObserver?.disconnect();
      this.headerObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }
  });
}
